import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { memo } from 'react';
import { T } from '../../translation/src';
import { Tooltip } from '../Tooltip';

export interface CreditBalanceProps {
  balance: number;
  // What is left of this month's allowance (it expires first); absent without one.
  monthlyRemaining?: number | null;
  // Projects already spoken for by uploads under way, and what a new upload may draw on.
  pending?: number;
  available?: number;
}

// The balance, with the expiring monthly part split from the rest behind an
// info icon. Shared by the subscription page and the superadmin credit modal.
const CreditBalance_: React.FC<CreditBalanceProps> = ({ balance, monthlyRemaining, pending = 0, available }) => {
  // Without an allowance (FLEX, expired) nothing to split.
  const hasAllowance = monthlyRemaining != null;
  const monthlyLeft = monthlyRemaining ?? 0;
  // Everything that is not this month's allowance: bought, granted, or the welcome project.
  const otherLeft = Math.max(0, balance - monthlyLeft);
  const showDetails = hasAllowance || pending > 0;
  return (
    <div className="flex items-center gap-1">
      <span className="text-2xl font-semibold">
        {balance} {balance === 1 ? <T _str="project left" /> : <T _str="projects left" />}
      </span>
      {showDetails && (
        <Tooltip
          placement="top"
          className="p-2"
          message={
            <div className="grid grid-cols-[auto_auto] gap-x-3 text-xs">
              {hasAllowance && (
                <>
                  <span className="text-gray-500">
                    <T _str="monthly" swc />:
                  </span>
                  <span className="text-right font-medium">{monthlyLeft}</span>
                  <span className="text-gray-500">
                    <T _str="other" swc />:
                  </span>
                  <span className="text-right font-medium">{otherLeft}</span>
                </>
              )}
              {pending > 0 && (
                <>
                  <span className="text-gray-500">
                    <T _str="reserved by uploads" swc />:
                  </span>
                  <span className="text-right font-medium">−{pending}</span>
                  <span className="text-gray-500">
                    <T _str="available" swc />:
                  </span>
                  <span className="text-right font-medium">{available ?? Math.max(0, balance - pending)}</span>
                </>
              )}
            </div>
          }
        >
          <InformationCircleIcon className="w-4 h-4 text-gray-500" />
        </Tooltip>
      )}
    </div>
  );
};

export const CreditBalance = memo(CreditBalance_);
